import type { ElementType } from 'react'
import { cn } from '@/lib/cn'
import type { TypographyVariant, TypographyColor } from './Typography.types'

const VARIANT_CLASSES: Record<TypographyVariant, string> = {
  'h1':             'text-2xl font-extrabold',
  'h2':             'text-xl  font-bold',
  'h3':             'text-lg  font-bold',
  'h4':             'text-base font-bold',
  'subtitle':       'text-base font-semibold',
  'body':           'text-sm',
  'body-semibold':  'text-sm  font-semibold',
  'body-bold':      'text-sm  font-bold',
  'caption':        'text-xs',
  'caption-semibold': 'text-xs font-semibold',
  'caption-bold':   'text-xs  font-bold',
  'label':          'text-xs  font-extrabold uppercase tracking-widest',
  'overline':       'text-xs  font-semibold  uppercase tracking-wider',
  'card-title':     'text-[11px] font-semibold uppercase tracking-widest',
  'mono':           'text-xs  font-mono',
  'amount':         'text-4xl font-extrabold tracking-tight',
  'amount-md':      'text-3xl font-extrabold tracking-tight',
  'amount-sm':      'text-xl  font-extrabold',
}

const COLOR_CLASSES: Record<TypographyColor, string> = {
  primary:   'text-[var(--color-text-primary)]',
  secondary: 'text-[var(--color-text-secondary)]',
  muted:     'text-[var(--color-text-muted)]',
  disabled:  'text-[var(--color-text-disabled)]',
  accent:    'text-[var(--color-accent)]',
  success:   'text-[var(--color-success)]',
  danger:    'text-[var(--color-danger)]',
  warning:   'text-[var(--color-warning,#f59e0b)]',
  inherit:   '',
}

const DEFAULT_TAG: Record<TypographyVariant, ElementType> = {
  'h1':             'h1',
  'h2':             'h2',
  'h3':             'h3',
  'h4':             'h4',
  'subtitle':       'p',
  'body':           'p',
  'body-semibold':  'p',
  'body-bold':      'p',
  'caption':        'span',
  'caption-semibold': 'span',
  'caption-bold':   'span',
  'label':          'span',
  'overline':       'span',
  'card-title':     'p',
  'mono':           'span',
  'amount':         'p',
  'amount-md':      'p',
  'amount-sm':      'p',
}

export function useTypography(
  variant: TypographyVariant = 'body',
  color: TypographyColor = 'primary',
  className?: string,
) {
  return {
    classes:    cn(VARIANT_CLASSES[variant], COLOR_CLASSES[color], className),
    defaultTag: DEFAULT_TAG[variant],
  }
}
